import {useEffect} from 'react';
import {useBoosterFlow} from '../../../../context/BoosterFlowContext';

/**
 * Qué acto del Recorrido se está mirando, publicado en `BoosterFlowContext`.
 *
 * Desde que el Recorrido es UNA página, el paso activo ya no se puede leer de la URL: los cuatro
 * actos comparten el mismo path y el hash sólo dice por dónde se ENTRÓ. Quien lo sabe es esto, y
 * quien necesita saberlo (el grupo del sidebar) es hermano del contenido, no su hijo — de ahí el
 * contexto en el medio.
 *
 * Tres cosas que no son obvias:
 *
 * 1. **El scroller NO es la ventana.** Es `.app-content`, que tiene `overflow-x: hidden` y por spec
 *    eso hace que el navegador compute `overflow-y: auto`. Un IntersectionObserver con `root: null`
 *    mide contra el viewport, que acá no se mueve: las secciones "entran" y "salen" en momentos que
 *    no tienen nada que ver con lo que el usuario ve. Si el contenedor no aparece (montaje fuera
 *    del marco), se cae a la ventana, que es el comportamiento de siempre.
 *
 * 2. **El activo sale de `entry.target.id`, nunca del índice del entry.** El callback recibe SÓLO
 *    las secciones que cambiaron —a veces una sola—, así que leer `ids[i]` sobre ese array devuelve
 *    la sección equivocada apenas hay más de un acto. Es el bug del mockup, y se agrava porque
 *    ordena bien de casualidad mientras los actos estén los cuatro y en orden.
 *
 * 3. **El fondo de la página fuerza el último acto.** El destino ("Usa tu IA") es corto y vive
 *    abajo de todo: con la franja del observer recortada arriba y abajo, no hay scroll posible que
 *    lo haga cruzar el centro. Sin esto, llegar al final del Recorrido deja el sidebar marcando
 *    Memoria para siempre.
 */

// La franja del mockup: sólo la sección que cruza la banda centro-alta gana. Sin recorte, con dos
// actos visibles a la vez gana el que empiece más arriba aunque ocupe una línea de la pantalla.
export const SECTION_BAND = '-38% 0px -55% 0px';

// Cuánto falta para considerar que se llegó al fondo. Un umbral de 0 no se alcanza nunca: el
// scroll con rueda o trackpad deja fracciones de píxel.
const BOTTOM_SLACK = 24;

/** El contenedor que scrollea de verdad en el marco 2.1, o `null` (la ventana) como fallback. */
export function scrollerFor(node) {
    if (!node || typeof node.closest !== 'function') return null;
    return node.closest('.app-content');
}

export function useSectionObserver(containerRef, sectionIds) {
    const {setActiveSection} = useBoosterFlow();
    // Los ids llegan como array nuevo en cada render; el efecto se re-arma sólo si CAMBIAN.
    const idsKey = (sectionIds || []).join('|');

    useEffect(() => {
        const ids = idsKey ? idsKey.split('|') : [];
        if (!ids.length) return undefined;

        // jsdom no trae IntersectionObserver (y algún navegador viejo tampoco). Sin él no hay forma
        // de saber qué se ve: el primer acto queda activo y la página funciona igual, sin crash.
        if (typeof window.IntersectionObserver !== 'function') {
            setActiveSection(ids[0]);
            return () => setActiveSection(null);
        }

        const scroller = scrollerFor(containerRef && containerRef.current);
        const nodes = ids.map(id => document.getElementById(id)).filter(Boolean);
        if (!nodes.length) return undefined;

        const inBand = new Set();
        let atBottom = false;

        const publish = () => {
            if (atBottom) {
                setActiveSection(ids[ids.length - 1]);
                return;
            }
            // El primero del recorrido que esté en la franja. Cuando no hay ninguno (el hueco entre
            // dos actos) se conserva el último: apagarlo dejaría el sidebar sin marca a mitad de
            // scroll, que se lee como un salto.
            const next = ids.find(id => inBand.has(id));
            if (next) setActiveSection(next);
        };

        const observer = new window.IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const id = entry.target && entry.target.id;
                if (!id) return;
                if (entry.isIntersecting) inBand.add(id);
                else inBand.delete(id);
            });
            publish();
        }, {root: scroller || null, rootMargin: SECTION_BAND, threshold: 0});

        nodes.forEach(node => observer.observe(node));

        const metrics = () => {
            if (scroller) {
                return {top: scroller.scrollTop, view: scroller.clientHeight, total: scroller.scrollHeight};
            }
            const doc = document.documentElement || document.body;
            return {
                top: window.pageYOffset || doc.scrollTop || 0,
                view: window.innerHeight || doc.clientHeight || 0,
                total: doc.scrollHeight || 0,
            };
        };

        const onScroll = () => {
            const {top, view, total} = metrics();
            const bottom = total - (top + view) <= BOTTOM_SLACK;
            if (bottom === atBottom) return;
            atBottom = bottom;
            // El observer sigue vivo y puede reportar después que el fondo se alcanzó: `atBottom`
            // se consulta dentro de `publish`, así que gana mientras dure.
            publish();
        };

        const target = scroller || window;
        target.addEventListener('scroll', onScroll, {passive: true});

        return () => {
            observer.disconnect();
            target.removeEventListener('scroll', onScroll);
            // El provider vive en `App.js` y NO remonta al navegar: sin esto, salir del Recorrido
            // deja al sidebar marcando un acto de una página que ya no está.
            setActiveSection(null);
        };
    }, [idsKey, containerRef, setActiveSection]);
}

export default useSectionObserver;
